/*
 * Global keyboard-shortcut manager.
 *
 * A single `keydown` listener at the window level dispatches to registered
 * handlers. Components register via `useShortcut(spec, handler, options)` on
 * mount; handlers are cleaned up on unmount.
 *
 * Shortcut strings are small and obvious:
 *   "mod+k"        — ⌘ on Mac, Ctrl on Win/Linux
 *   "n"            — plain letter (fires only when not typing)
 *   "g d"          — chord (press "g", then "d" within 1s)
 *   "shift+?"      — shift-modified
 *   "alt+ArrowUp"  — modifier + named key (case-sensitive for the base key)
 *   "Escape"       — named key, no modifier
 *   "Plus"         — the + key, which a shortcut string cannot hold ("+" joins modifiers)
 *
 * Global shortcuts are suppressed while an interactive control owns focus.
 * A registration may opt in with `allowWhileTyping` for controls such as
 * dialog Escape handlers and the command palette.
 *
 * Handlers can call `preventDefault()` on the event to stop the browser's
 * default behaviour — the manager already does this for matched shortcuts
 * unless `preventDefault: false` is passed.
 */

import { useEffect, useRef } from 'react';

export interface ShortcutOptions {
  /** Pass false to skip preventDefault on the raw KeyboardEvent. */
  preventDefault?: boolean;
  /** Pass false to disable the handler without unregistering. */
  enabled?: boolean;
  /** Fire even while a typing target is focused. Defaults to false. */
  allowWhileTyping?: boolean;
  /** Priority: higher runs first; once a handler matches, later ones are skipped. */
  priority?: number;
  /**
   * Fire while a control inside an element matching this selector has focus
   * (the listening ribbon's buttons: Space there plays and pauses rather than
   * pressing the button again). Text fields never qualify.
   */
  allowWithin?: string;
}

type Handler = (e: KeyboardEvent) => void;

interface Registration {
  spec: ParsedShortcut[];
  handler: Handler;
  opts: Required<Omit<ShortcutOptions, 'enabled' | 'allowWithin'>> & { enabled: boolean; allowWithin: string | null };
}

interface ParsedShortcut {
  key: string;
  mod: boolean;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

const registrations: Registration[] = [];
let chordBuffer: ParsedShortcut | null = null;
let chordTimer: ReturnType<typeof setTimeout> | null = null;

function parseToken(token: string): ParsedShortcut {
  const parts = token.split('+').map((p) => p.trim());
  const spec: ParsedShortcut = {
    key: '',
    mod: false,
    ctrl: false,
    alt: false,
    shift: false,
    meta: false,
  };
  for (const p of parts) {
    const lower = p.toLowerCase();
    if (lower === 'mod') spec.mod = true;
    else if (lower === 'ctrl') spec.ctrl = true;
    else if (lower === 'alt' || lower === 'option') spec.alt = true;
    else if (lower === 'shift') spec.shift = true;
    else if (lower === 'meta' || lower === 'cmd' || lower === '⌘') spec.meta = true;
    else spec.key = p; // preserve case for named keys like "ArrowUp"
  }
  // Normalize single-char keys to lowercase so "N" and "n" match the same way.
  if (spec.key.length === 1) spec.key = spec.key.toLowerCase();
  return spec;
}

function parseShortcut(shortcut: string): ParsedShortcut[] {
  // Chord: space-separated sequence.
  return shortcut
    .split(' ')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => parseToken(t === '+' ? 'Plus' : t));
}

function matches(spec: ParsedShortcut, e: KeyboardEvent): boolean {
  // The space bar's key is ' ', which a shortcut string cannot hold (a space separates a chord); it is named "Space".
  // Likewise '+' joins modifiers, so the plus key is named "Plus".
  const key = e.key === ' ' ? 'Space' : e.key === '+' ? 'Plus' : e.key;
  const specKey = spec.key;
  const isSingleChar = specKey.length === 1;
  const eventKey = isSingleChar ? key.toLowerCase() : key;
  if (eventKey !== specKey && !(specKey === '?' && key === '?')) return false;
  const modPressed = IS_MAC ? e.metaKey : e.ctrlKey;
  if (spec.mod && !modPressed) return false;
  if (spec.ctrl && !e.ctrlKey) return false;
  if (spec.meta && !e.metaKey) return false;
  if (spec.alt && !e.altKey) return false;
  // Shift is tricky: when the base key already implies shift (e.g. '?'), the
  // event's shiftKey will be true regardless. Only enforce "shift must not be
  // pressed" for plain single letters without an explicit shift in the spec.
  if (spec.shift && !e.shiftKey) return false;
  if (!spec.shift && isSingleChar && e.shiftKey && specKey !== '?' && specKey !== '/') {
    // Block accidental matches when user types Shift+letter for a capital.
    return false;
  }
  // Disallow mod/ctrl/meta/alt press unless the spec asked for them.
  if (!spec.mod && !spec.ctrl && !spec.meta && (modPressed || e.metaKey)) {
    if (!(spec.alt && e.altKey)) return false;
  }
  if (!spec.alt && e.altKey) return false;
  return true;
}

const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'form',
  'input',
  'select',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="combobox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
].join(',');

function isInteractiveTarget(target: EventTarget | Element | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.matches(INTERACTIVE_SELECTOR) || target.closest(INTERACTIVE_SELECTOR) !== null;
}

const TYPING_SELECTOR = 'input, select, textarea, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"]';

/** Focus is on a control inside `selector`, and not one that takes typing. */
function focusWithin(e: KeyboardEvent, selector: string): boolean {
  const el = document.activeElement instanceof Element ? document.activeElement : e.target instanceof Element ? e.target : null;
  return !!el && el.closest(selector) !== null && el.closest(TYPING_SELECTOR) === null;
}

function dispatch(e: KeyboardEvent) {
  if (e.defaultPrevented || e.isComposing || e.repeat) return;

  const interactive =
    isInteractiveTarget(e.target) || isInteractiveTarget(document.activeElement);

  for (const reg of registrations.slice().sort((a, b) => b.opts.priority - a.opts.priority)) {
    if (!reg.opts.enabled) continue;
    if (interactive && !reg.opts.allowWhileTyping && !(reg.opts.allowWithin && focusWithin(e, reg.opts.allowWithin))) continue;

    if (reg.spec.length === 1) {
      if (matches(reg.spec[0]!, e)) {
        if (reg.opts.preventDefault) e.preventDefault();
        reg.handler(e);
        return;
      }
    } else if (reg.spec.length === 2) {
      // Chord: expect first in buffer, second on this event.
      if (chordBuffer && chordEqual(chordBuffer, reg.spec[0]!) && matches(reg.spec[1]!, e)) {
        if (reg.opts.preventDefault) e.preventDefault();
        reg.handler(e);
        clearChord();
        return;
      }
    }
  }

  // No chord handler fired — if this keystroke is the *start* of any
  // registered chord, buffer it.
  const isChordStart = registrations.some(
    (r) =>
      r.opts.enabled
      && r.spec.length === 2
      && matches(r.spec[0]!, e)
      && (!interactive || r.opts.allowWhileTyping),
  );
  if (isChordStart) {
    chordBuffer = snapshotSpec(e);
    if (chordTimer) clearTimeout(chordTimer);
    chordTimer = setTimeout(clearChord, 1000);
    return;
  }
  clearChord();
}

function chordEqual(a: ParsedShortcut, b: ParsedShortcut): boolean {
  return (
    a.key === b.key &&
    a.mod === b.mod &&
    a.ctrl === b.ctrl &&
    a.alt === b.alt &&
    a.shift === b.shift &&
    a.meta === b.meta
  );
}

function snapshotSpec(e: KeyboardEvent): ParsedShortcut {
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  return {
    key,
    mod: IS_MAC ? e.metaKey : e.ctrlKey,
    ctrl: e.ctrlKey,
    alt: e.altKey,
    shift: e.shiftKey,
    meta: e.metaKey,
  };
}

function clearChord() {
  chordBuffer = null;
  if (chordTimer) {
    clearTimeout(chordTimer);
    chordTimer = null;
  }
}

let listenerAttached = false;
function ensureListener() {
  if (listenerAttached || typeof window === 'undefined') return;
  window.addEventListener('keydown', dispatch, true);
  listenerAttached = true;
}

/**
 * React hook: register a keyboard shortcut for the lifetime of the component.
 * `handler` is captured in a ref so callers don't need to memoize it.
 */
export function useShortcut(
  shortcut: string,
  handler: Handler,
  options: ShortcutOptions = {},
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    ensureListener();
    const reg: Registration = {
      spec: parseShortcut(shortcut),
      handler: (e) => handlerRef.current(e),
      opts: {
        preventDefault: options.preventDefault ?? true,
        enabled: options.enabled ?? true,
        allowWhileTyping: options.allowWhileTyping ?? false,
        priority: options.priority ?? 0,
        allowWithin: options.allowWithin ?? null,
      },
    };
    registrations.push(reg);
    return () => {
      const idx = registrations.indexOf(reg);
      if (idx >= 0) registrations.splice(idx, 1);
    };
    // Re-register if the shortcut string changes or enable flag flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcut, options.enabled, options.allowWhileTyping, options.priority, options.preventDefault, options.allowWithin]);
}

/** Render-safe display of a shortcut token, e.g. "⌘K" on Mac, "Ctrl+K" elsewhere. */
export function formatShortcut(shortcut: string): string {
  const tokens = shortcut.split(' ');
  return tokens
    .map((t) =>
      t
        .split('+')
        .map((p) => {
          const low = p.toLowerCase();
          if (low === 'mod') return IS_MAC ? '⌘' : 'Ctrl';
          if (low === 'cmd' || low === 'meta') return '⌘';
          if (low === 'alt' || low === 'option') return IS_MAC ? '⌥' : 'Alt';
          if (low === 'shift') return IS_MAC ? '⇧' : 'Shift';
          if (low === 'ctrl') return 'Ctrl';
          if (low === 'arrowup') return '↑';
          if (low === 'arrowdown') return '↓';
          if (low === 'arrowleft') return '←';
          if (low === 'arrowright') return '→';
          if (low === 'escape') return 'Esc';
          if (low === 'enter') return '↵';
          if (low === 'plus') return '+';
          return p.length === 1 ? p.toUpperCase() : p;
        })
        .join(IS_MAC ? '' : '+'),
    )
    .join(' ');
}
